import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { useQueryClient } from "react-query";
import MeaButton from "../../../../components/MeaButton";
import studentsAPI from "../../../../services/students";
import { useAuthContext } from "../../../../hooks/useAuthContext";
import { PERMISSION_TRAINING_STUDENT_OBSERVATIONS } from "../../../../permissions";

export const OBSERVATION_MAX_LENGTH = 5000;

const AddStudentObservationModal = forwardRef(({ schoolId, trainingId, onCreated }, ref) => {
  const toast = useRef(null);
  const queryClient = useQueryClient();
  const [visible, setVisible] = useState(false);
  const [student, setStudent] = useState(null);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(null);
  const { hasPermission } = useAuthContext();
  const canNotify = hasPermission(PERMISSION_TRAINING_STUDENT_OBSERVATIONS.NOTIFY);

  useImperativeHandle(ref, () => ({
    show: (studentData) => {
      setStudent(studentData || null);
      setMessage("");
      setVisible(true);
    },
    hide: () => setVisible(false),
  }));

  const trimmedMessage = message.trim();
  const canSave = !!student?.id && trimmedMessage.length > 0 && message.length <= OBSERVATION_MAX_LENGTH && !saving;

  const studentName = [student?.firstname, student?.lastname].filter(Boolean).join(" ");

  const handleSave = async (notify) => {
    if (!canSave) {
      return;
    }
    setSaving(notify ? "notify" : "save");
    let created = null;
    try {
      const result = await studentsAPI.CreateObservation(schoolId, trainingId, "", student.id, trimmedMessage);
      created = result?.data;
      if (notify && created?.id) {
        const notified = await studentsAPI.NotifyObservation(schoolId, trainingId, "", student.id, created.id);
        created = notified?.data || created;
      }
      toast.current?.show({
        severity: "success",
        summary: "Observation ajoutée",
        detail: notify
          ? "L'observation est partagée avec la famille et les parents ont été notifiés."
          : "L'observation reste interne à l'équipe.",
      });
      setVisible(false);
      onCreated?.(created);
    } catch (err) {
      if (created) {
        // The observation was saved but the notification failed.
        setVisible(false);
        onCreated?.(created);
      }
      toast.current?.show({ severity: "error", summary: "Erreur", detail: err.message });
    } finally {
      queryClient.invalidateQueries(["student-observations", schoolId, trainingId, student.id]);
      queryClient.invalidateQueries(["training-student-observations", schoolId, trainingId]);
      setSaving(null);
    }
  };

  return (
    <>
      <Toast ref={toast} position="bottom-center" />
      <Dialog
        header={studentName ? `Nouvelle observation — ${studentName}` : "Nouvelle observation"}
        visible={visible}
        style={{ width: "45vw" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        modal
        onHide={() => {
          if (!saving) {
            setVisible(false);
          }
        }}
        footer={
          <div className="flex justify-content-end gap-2">
            <MeaButton label="Annuler" icon="pi pi-times" className="p-button-text" disabled={!!saving} onClick={() => setVisible(false)} />
            <MeaButton
              label="Enregistrer"
              icon="pi pi-check"
              outlined={canNotify}
              loading={saving === "save"}
              disabled={!canSave}
              onClick={() => handleSave(false)}
            />
            {canNotify && (
              <MeaButton
                label="Enregistrer et notifier les parents"
                icon="pi pi-send"
                loading={saving === "notify"}
                disabled={!canSave}
                onClick={() => handleSave(true)}
              />
            )}
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          <div className="surface-50 border-1 border-200 border-round p-3 text-sm text-700 line-height-3">
            <i className="pi pi-info-circle mr-2"></i>
            L'observation reste interne : elle n'est visible que par l'équipe (administration, secrétariat,
            enseignant(e)s). Elle ne sera visible par la famille qu'après avoir cliqué sur « Notifier les
            parents » : un email est alors envoyé immédiatement aux parents (et à l'élève s'il a un email),
            sans mentionner votre nom.
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor="observation-message" className="font-medium">
              Message
            </label>
            <InputTextarea
              id="observation-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              autoResize
              maxLength={OBSERVATION_MAX_LENGTH}
              placeholder="Saisissez votre observation..."
              className="w-full"
            />
            <small className="text-500 text-right">
              {message.length} / {OBSERVATION_MAX_LENGTH}
            </small>
          </div>
        </div>
      </Dialog>
    </>
  );
});

export default AddStudentObservationModal;
